import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import {
  DEFAULT_FORCE_VALIDITY_MONTHS,
  addForceValidityDaysToIsoDate,
  calculateForceResults,
  configureForceStandard,
  createForceSession,
  forcePointsToRows,
  getForceSession,
  listForceBlocks,
  listForcePoints,
  listForceResults,
  listForceSessionsByInstrument,
  listForceStandardOptions,
  saveForcePoints,
  syncForceDirections,
  updateForceSession,
} from '../../utils/api-force-calibration';
import {
  computeForcePointMetrics,
  finiteOrNull,
  mergeForceFamilyData,
} from '../../utils/force-calibration-profile';

const VALIDITY = [
  { value: 6, label: '6 meses' },
  { value: 12, label: '1 año' },
  { value: 24, label: '2 años' },
];
const todayIso = () => new Date().toISOString().slice(0, 10);
const fmt = (value, decimals = 4) => finiteOrNull(value) === null ? '—' : Number(value).toFixed(decimals).replace('.', ',');
const environmentAverage = (readings, field) => {
  const values = (readings || []).map((reading) => finiteOrNull(reading?.[field])).filter((value) => value !== null);
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
};

export default function ForceCalibrationPage() {
  const { instrumentId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const serviceOrderCode = searchParams.get('serviceOrderCode') || '';
  const initialSessionId = searchParams.get('sessionId') || '';
  const templateCode = searchParams.get('templateCode') || '';

  const [instrument, setInstrument] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(initialSessionId);
  const [session, setSession] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [rowsByBlock, setRowsByBlock] = useState({});
  const [options, setOptions] = useState([]);
  const [selectedCapabilityId, setSelectedCapabilityId] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState(null);

  const familyData = useMemo(() => mergeForceFamilyData(session?.family_data), [session?.family_data]);
  const standardSnapshot = familyData.standard_configuration?.reference || null;
  const unit = familyData.values_normalized_to || session?.instrument_unit || 'Nm';
  const isTorque = familyData.subfamily === 'torque';
  const repeatCount = Math.max(1, Number(familyData.repeat_count) || 5);

  const liveResults = useMemo(() => {
    if (!session || !standardSnapshot) return [];
    return blocks.flatMap((block) => (rowsByBlock[block.id] || [])
      .map((row) => computeForcePointMetrics(row, session, standardSnapshot, block))
      .filter(Boolean));
  }, [blocks, rowsByBlock, session, standardSnapshot]);

  async function loadSessions(preferred = '') {
    const list = await listForceSessionsByInstrument(instrumentId);
    setSessions(list);
    if (preferred) setSessionId(preferred);
    else if (!sessionId && list.length) setSessionId(list[0].id);
  }

  async function loadSession(id) {
    if (!id) {
      setSession(null); setBlocks([]); setRowsByBlock({}); setOptions([]); setResults([]);
      return;
    }
    const [loaded, loadedBlocks, points, calculated] = await Promise.all([
      getForceSession(id), listForceBlocks(id), listForcePoints(id), listForceResults(id),
    ]);
    const normalized = { ...loaded, family_data: mergeForceFamilyData(loaded.family_data) };
    setSession(normalized);
    setBlocks(loadedBlocks);
    setRowsByBlock(Object.fromEntries(loadedBlocks.map((block) => [
      block.id,
      forcePointsToRows(block, points, normalized),
    ])));
    const selected = normalized.family_data.standard_configuration?.reference?.capability?.id || '';
    setSelectedCapabilityId(selected);
    setResults(calculated || []);
    try {
      setOptions(await listForceStandardOptions({ session: normalized, onDate: normalized.calibration_date || todayIso() }));
    } catch (error) {
      setOptions([]);
      setNotice({ type: 'error', text: error.message });
    }
  }

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data, error } = await supabase.from('instruments')
          .select('*, category:category_id(name), client:client_id(id, name, client_number)')
          .eq('id', instrumentId).single();
        if (error) throw error;
        if (!active) return;
        setInstrument(data);
        await loadSessions();
      } catch (error) {
        if (active) setNotice({ type: 'error', text: error.message });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [instrumentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sessionId) return;
    loadSession(sessionId).catch((error) => setNotice({ type: 'error', text: error.message }));
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  function patchSession(field, value) {
    setSession((current) => ({ ...current, [field]: value }));
  }

  function patchFamily(field, value) {
    setSession((current) => ({
      ...current,
      family_data: mergeForceFamilyData({ ...current.family_data, [field]: value }),
    }));
  }

  function patchBudget(field, value) {
    setSession((current) => ({
      ...current,
      family_data: mergeForceFamilyData({
        ...current.family_data,
        standard_budget: { ...current.family_data?.standard_budget, [field]: value },
      }),
    }));
  }

  function patchRow(blockId, rowIndex, field, value) {
    setRowsByBlock((current) => ({
      ...current,
      [blockId]: (current[blockId] || []).map((row, index) => index === rowIndex ? { ...row, [field]: value } : row),
    }));
  }

  async function handleCreate() {
    setBusy('create'); setNotice(null);
    try {
      const created = await createForceSession({ instrumentId, serviceOrderCode, templateCode });
      await loadSessions(created.id);
      setNotice({ type: 'success', text: 'Sesión de Fuerza / Torque creada.' });
    } catch (error) {
      setNotice({ type: 'error', text: error.message });
    } finally { setBusy(''); }
  }

  async function handleDirections() {
    if (!session) return;
    setBusy('directions'); setNotice(null);
    try {
      await syncForceDirections(session, familyData.directions);
      await loadSession(session.id);
      setNotice({ type: 'success', text: 'Sentidos de calibración actualizados.' });
    } catch (error) {
      setNotice({ type: 'error', text: error.message });
    } finally { setBusy(''); }
  }

  async function handleConfigureStandard() {
    if (!session || !selectedCapabilityId) return;
    setBusy('standard'); setNotice(null);
    try {
      await configureForceStandard(session.id, selectedCapabilityId);
      await loadSession(session.id);
      setNotice({ type: 'success', text: 'Patrón fijado y trazabilidad validada para la sesión.' });
    } catch (error) {
      setNotice({ type: 'error', text: error.message });
    } finally { setBusy(''); }
  }

  async function persist(calculate) {
    if (!session || !blocks.length) return;
    setBusy(calculate ? 'calculate' : 'save'); setNotice(null);
    try {
      const calibrationDate = session.calibration_date || todayIso();
      const validityMonths = Number(session.validity_months) || DEFAULT_FORCE_VALIDITY_MONTHS;
      const environmentReadings = familyData.environment_readings || [];
      const updated = await updateForceSession(session.id, {
        instrument_range_min: finiteOrNull(session.instrument_range_min),
        instrument_range_max: finiteOrNull(session.instrument_range_max),
        instrument_resolution: finiteOrNull(session.instrument_resolution),
        instrument_class: finiteOrNull(session.instrument_class),
        instrument_location: session.instrument_location || null,
        calibration_date: calibrationDate,
        validity_months: validityMonths,
        valid_until: addForceValidityDaysToIsoDate(calibrationDate, validityMonths),
        ambient_temp_avg: environmentAverage(environmentReadings, 'temp_c'),
        ambient_pressure_avg: environmentAverage(environmentReadings, 'pressure_hpa'),
        ambient_humidity_avg: environmentAverage(environmentReadings, 'humidity_rh'),
        notes: session.notes || null,
        family_data: familyData,
      });
      for (const block of blocks) {
        await saveForcePoints(updated.id, block, rowsByBlock[block.id] || [], updated.family_data);
      }
      if (calculate) await calculateForceResults(updated.id);
      await loadSession(updated.id);
      await loadSessions(updated.id);
      setNotice({ type: 'success', text: calculate ? 'Cálculo finalizado y enviado a Certificados a aprobar.' : 'Borrador guardado.' });
    } catch (error) {
      setNotice({ type: 'error', text: error.message });
    } finally { setBusy(''); }
  }

  function toggleDirection(direction) {
    const current = familyData.directions || [];
    const next = current.includes(direction) ? current.filter((item) => item !== direction) : [...current, direction];
    patchFamily('directions', next);
  }

  if (loading) return <div className="p-8 text-slate-200">Cargando módulo de Fuerza / Torque…</div>;
  const calibrationDate = session?.calibration_date || todayIso();
  const validUntil = addForceValidityDaysToIsoDate(calibrationDate, session?.validity_months || DEFAULT_FORCE_VALIDITY_MONTHS);

  return (
    <div className="p-6 space-y-6 max-w-[1800px] mx-auto">
      <style>{`.input-force{display:block;width:100%;margin-top:.25rem;border:1px solid rgb(203 213 225);border-radius:.25rem;background:#fff;padding:.375rem .5rem;color:#0f172a}.force-cell{border:1px solid rgba(255,255,255,.12);padding:.5rem}`}</style>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-amber-300">Fuerza / Torque · Comparación directa</div>
          <h1 className="text-2xl font-semibold text-white">{isTorque ? 'Calibración de torquímetros' : 'Calibración de fuerza'}</h1>
          <p className="text-sm text-slate-300 mt-1">Instrumento <strong>{instrument?.tag || instrument?.lab_id || instrumentId}</strong> · {instrument?.brand || '—'} {instrument?.model || ''} · Cliente: {instrument?.client?.name || '—'}</p>
        </div>
        <button type="button" onClick={() => navigate(`/ops/instrument-details/${instrumentId}`)} className="px-3 py-2 rounded-lg border border-white/15 text-slate-200">Volver al instrumento</button>
      </div>

      {notice && <div className={`rounded-lg border px-4 py-3 text-sm ${notice.type === 'success' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200' : 'border-rose-500/30 bg-rose-500/10 text-rose-200'}`}>{notice.text}</div>}

      <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-4">
        <div className="flex items-end gap-3 flex-wrap">
          <label className="text-xs text-slate-300">Sesión
            <select value={sessionId} onChange={(event) => setSessionId(event.target.value)} className="block mt-1 min-w-[360px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900">
              <option value="">Seleccionar…</option>
              {sessions.map((item) => <option key={item.id} value={item.id}>{new Date(item.created_at).toLocaleString('es-AR')} · {item.status}</option>)}
            </select>
          </label>
          <button type="button" disabled={!!busy} onClick={handleCreate} className="px-4 py-2 rounded-lg bg-amber-600 text-white font-medium disabled:opacity-60">{busy === 'create' ? 'Creando…' : 'Nueva sesión'}</button>
        </div>

        {session && <>
          {session.review_notes && <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200"><strong>Observación del jefe:</strong> {session.review_notes}</div>}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
            <ReadField label="Estado" value={session.status} />
            <ReadField label="Unidad normalizada" value={unit} />
            <NumberField label={`Mínimo (${unit})`} value={session.instrument_range_min} onChange={(value) => patchSession('instrument_range_min', value)} />
            <NumberField label={`Máximo (${unit})`} value={session.instrument_range_max} onChange={(value) => patchSession('instrument_range_max', value)} />
            <NumberField label={`Resolución (${unit})`} value={session.instrument_resolution} onChange={(value) => patchSession('instrument_resolution', value)} />
            <label className="text-xs text-slate-300">Fecha<input type="date" value={calibrationDate} onChange={(event) => patchSession('calibration_date', event.target.value)} className="input-force" /></label>
            <label className="text-xs text-slate-300">Validez<select value={session.validity_months || 12} onChange={(event) => patchSession('validity_months', Number(event.target.value))} className="input-force">{VALIDITY.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select><span className="block mt-1 text-[11px] text-slate-400">Hasta {validUntil}</span></label>
            <TextField label="Lugar" value={session.instrument_location || ''} onChange={(value) => patchSession('instrument_location', value)} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
            {isTorque && <label className="text-xs text-slate-300">Tipo<select value={familyData.tool_type || 'II'} onChange={(event) => patchFamily('tool_type', event.target.value)} className="input-force"><option value="I">Tipo I · lectura directa</option><option value="II">Tipo II · disparo</option></select></label>}
            {isTorque && <label className="text-xs text-slate-300">Clase<select value={familyData.tool_class || ''} onChange={(event) => patchFamily('tool_class', event.target.value)} className="input-force"><option value="">Manual</option>{['A', 'B', 'C', 'D', 'E', 'F', 'G'].map((item) => <option key={item} value={item}>{item}</option>)}</select></label>}
            <label className="text-xs text-slate-300">Tolerancia<select value={familyData.tolerance_mode} onChange={(event) => patchFamily('tolerance_mode', event.target.value)} className="input-force"><option value="class">Por clase / porcentaje</option><option value="percent">Porcentaje manual</option><option value="absolute">Valor absoluto</option></select></label>
            {familyData.tolerance_mode === 'absolute'
              ? <NumberField label={`Tolerancia (${unit})`} value={session.instrument_class} onChange={(value) => patchSession('instrument_class', value)} />
              : <NumberField label="Tolerancia manual (%)" value={familyData.tolerance_percent} onChange={(value) => patchFamily('tolerance_percent', value)} />}
            <label className="text-xs text-slate-300">Repeticiones<select value={repeatCount} onChange={(event) => patchFamily('repeat_count', Number(event.target.value))} className="input-force">{[3, 5, 10].map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
            <TextField label="Observaciones" value={session.notes || ''} onChange={(value) => patchSession('notes', value)} />
          </div>

          {isTorque && <div className="flex items-center gap-4 flex-wrap rounded-lg border border-white/10 bg-black/10 px-3 py-3">
            <span className="text-xs text-slate-300">Sentidos:</span>
            {['CW', 'CCW'].map((direction) => <label key={direction} className="text-sm text-slate-200 flex items-center gap-2"><input type="checkbox" checked={familyData.directions.includes(direction)} onChange={() => toggleDirection(direction)} />{direction === 'CW' ? 'Horario' : 'Antihorario'}</label>)}
            <button type="button" disabled={!!busy || !familyData.directions.length} onClick={handleDirections} className="px-3 py-1.5 rounded-lg border border-amber-400/40 text-amber-200 text-xs">{busy === 'directions' ? 'Aplicando…' : 'Aplicar sentidos'}</button>
          </div>}
        </>}
      </section>

      {session && <>
        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
          <div className="flex justify-between gap-3 flex-wrap">
            <div><h2 className="text-lg font-semibold text-white">Patrón de referencia</h2><p className="text-xs text-slate-400">Solo aparecen patrones activos, verificados, vigentes y con alcance suficiente.</p></div>
            <button type="button" onClick={() => navigate('/quality/reference-standards')} className="px-3 py-2 rounded-lg border border-white/15 text-slate-200 text-sm">Abrir Instrumentos Patrones</button>
          </div>
          {!options.length ? <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">No hay un patrón de {isTorque ? 'torque' : 'fuerza'} aprobado y vigente para este rango. Revisá FA-001/FA-002/FA-003 en Instrumentos Patrones y cargá un certificado con incertidumbre expandida.</div> : <div className="flex items-end gap-3 flex-wrap"><label className="text-xs text-slate-300 flex-1 min-w-[360px]">Patrón<select value={selectedCapabilityId} onChange={(event) => setSelectedCapabilityId(event.target.value)} className="input-force"><option value="">Seleccionar…</option>{options.map((option) => <option key={option.capability_id} value={option.capability_id}>{option.code} · {option.brand_model || option.asset_name} · {fmt(option.range_min, 2)} a {fmt(option.range_max, 2)} {option.unit} · Cert. {option.certificate_no}</option>)}</select></label><button type="button" disabled={!!busy || !selectedCapabilityId} onClick={handleConfigureStandard} className="px-4 py-2 rounded-lg bg-amber-600 text-white font-medium disabled:opacity-60">{busy === 'standard' ? 'Validando…' : 'Fijar patrón'}</button></div>}
          {standardSnapshot && <div className="text-xs text-emerald-300">✓ {standardSnapshot.asset?.code} · S/N {standardSnapshot.asset?.serial || '—'} · Certificado {standardSnapshot.certificate?.certificate_no}</div>}
        </section>

        {blocks.map((block) => <section key={block.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
          <div><h2 className="text-lg font-semibold text-white">Toma de datos · {block.display_name}</h2><p className="text-xs text-slate-400">Puntos sugeridos: 20 %, 60 % y 100 % para torque. Cada repetición registra patrón e instrumento en {unit}.</p></div>
          <div className="overflow-auto"><table className="min-w-[1500px] w-full border-collapse text-xs"><thead><tr className="bg-slate-800 text-slate-100"><th className="force-cell">Pto.</th><th className="force-cell">Nominal {unit}</th>{Array.from({ length: repeatCount }, (_, index) => <React.Fragment key={index}><th className="force-cell">Patrón R{index + 1}</th><th className="force-cell">Instrumento R{index + 1}</th></React.Fragment>)}</tr></thead><tbody>{(rowsByBlock[block.id] || []).map((row, rowIndex) => <tr key={row.point_no}><td className="force-cell text-center text-slate-300">{row.point_no}</td><Measurement value={row.setpoint} onChange={(value) => patchRow(block.id, rowIndex, 'setpoint', value)} />{Array.from({ length: repeatCount }, (_, index) => <React.Fragment key={index}><Measurement value={row[`r${index + 1}_pattern`]} onChange={(value) => patchRow(block.id, rowIndex, `r${index + 1}_pattern`, value)} /><Measurement value={row[`r${index + 1}_instrument`]} onChange={(value) => patchRow(block.id, rowIndex, `r${index + 1}_instrument`, value)} /></React.Fragment>)}</tr>)}</tbody></table></div>
        </section>)}

        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
          <h2 className="text-lg font-semibold text-white">Condiciones ambientales</h2>
          <div className="overflow-auto"><table className="min-w-[700px] w-full text-xs border-collapse"><thead><tr className="bg-slate-800">{['Momento', 'Temperatura °C', 'Presión hPa', 'Humedad %HR'].map((label) => <th key={label} className="force-cell">{label}</th>)}</tr></thead><tbody>{familyData.environment_readings.map((reading, index) => <tr key={reading.key}><td className="force-cell text-slate-300">{reading.label}</td>{['temp_c', 'pressure_hpa', 'humidity_rh'].map((field) => <Measurement key={field} value={reading[field]} onChange={(value) => { const next = familyData.environment_readings.map((item, rowIndex) => rowIndex === index ? { ...item, [field]: value } : item); patchFamily('environment_readings', next); }} />)}</tr>)}</tbody></table></div>
        </section>

        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
          <h2 className="text-lg font-semibold text-white">Presupuesto adicional</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3"><NumberField label="Deriva patrón (%)" value={familyData.standard_budget.drift_percent} onChange={(value) => patchBudget('drift_percent', value)} /><NumberField label="Coef. temperatura (% / 10 °C)" value={familyData.standard_budget.temperature_coefficient_percent_per_10c} onChange={(value) => patchBudget('temperature_coefficient_percent_per_10c', value)} /><NumberField label="Alineación (%)" value={familyData.standard_budget.alignment_uncertainty_percent} onChange={(value) => patchBudget('alignment_uncertainty_percent', value)} /><NumberField label="Otras contribuciones (%)" value={familyData.standard_budget.other_uncertainty_percent} onChange={(value) => patchBudget('other_uncertainty_percent', value)} /></div>
        </section>

        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
          <h2 className="text-lg font-semibold text-white">Resultados en vivo</h2>
          {!liveResults.length ? <p className="text-sm text-slate-400">Fijá un patrón con incertidumbre y completá las lecturas para calcular.</p> : <div className="overflow-auto"><table className="min-w-[1200px] w-full text-xs border-collapse"><thead><tr className="bg-slate-800">{['Sentido', 'Pto.', 'Patrón', 'Instrumento', 'Error', 'Error %', 'Repetibilidad', 'U (k=2)', 'Tolerancia', 'Conforme'].map((label) => <th key={label} className="force-cell">{label}</th>)}</tr></thead><tbody>{liveResults.map((result) => <tr key={`${result.block_id}-${result.point_no}`} className={result.in_tolerance === false ? 'bg-rose-500/10' : ''}><td className="force-cell">{result.direction}</td><td className="force-cell">{result.point_no}</td><td className="force-cell">{fmt(result.pattern_mean)}</td><td className="force-cell">{fmt(result.instrument_mean)}</td><td className="force-cell">{fmt(result.error_abs)}</td><td className="force-cell">{fmt(result.error_pct, 3)} %</td><td className="force-cell">{fmt(result.repeatability)}</td><td className="force-cell font-semibold">{fmt(result.u_reported)}</td><td className="force-cell">± {fmt(result.tolerance_abs)}</td><td className="force-cell text-center">{result.in_tolerance == null ? '—' : result.in_tolerance ? '✓' : '✕'}</td></tr>)}</tbody></table></div>}
          <div className="flex gap-3 flex-wrap"><button type="button" disabled={!!busy} onClick={() => persist(false)} className="px-4 py-2 rounded-lg border border-slate-400/50 bg-white/5 text-white font-semibold disabled:opacity-60">{busy === 'save' ? 'Guardando…' : 'Guardar borrador'}</button><button type="button" disabled={!!busy || !standardSnapshot} onClick={() => persist(true)} className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-60">{busy === 'calculate' ? 'Calculando…' : 'Calcular y enviar a aprobación'}</button>{results.length > 0 && <span className="self-center text-sm text-emerald-300">✓ Resultados calculados</span>}</div>
        </section>
      </>}
    </div>
  );
}

function Measurement({ value, onChange }) {
  return <td className="force-cell p-1"><input type="text" inputMode="decimal" value={value ?? ''} onChange={(event) => onChange(event.target.value)} className="w-full min-w-[88px] rounded border border-slate-300 bg-white px-2 py-1.5 text-right text-slate-900" /></td>;
}

function NumberField({ label, value, onChange }) {
  return <label className="text-xs text-slate-300">{label}<input type="number" step="any" value={value ?? ''} onChange={(event) => onChange(event.target.value)} className="input-force" /></label>;
}

function TextField({ label, value, onChange }) {
  return <label className="text-xs text-slate-300">{label}<input type="text" value={value ?? ''} onChange={(event) => onChange(event.target.value)} className="input-force" /></label>;
}

function ReadField({ label, value }) {
  return <div><div className="text-xs text-slate-300">{label}</div><div className="mt-1 min-h-[34px] rounded border border-white/15 bg-black/10 px-2 py-1.5 text-sm text-slate-100">{value ?? '—'}</div></div>;
}
